import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, SlidersHorizontal, Pencil, Trash2, Info } from "lucide-react";

type CustomField = {
  id: number;
  organizationId: number;
  entityType: string;
  fieldKey: string;
  label: string;
  fieldType: string;
  options: unknown;
  isRequired: boolean;
  sortOrder: number;
  createdAt: string | null;
};

type FieldForm = {
  label: string;
  fieldKey: string;
  fieldType: string;
  isRequired: boolean;
  sortOrder: number;
  options: string;
};

const emptyForm: FieldForm = {
  label: "",
  fieldKey: "",
  fieldType: "text",
  isRequired: false,
  sortOrder: 0,
  options: "",
};

function toSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

const fieldTypeLabels: Record<string, string> = {
  text: "Text",
  number: "Number",
  boolean: "Boolean",
  select: "Select",
  date: "Date",
};

function FieldList({ entityType }: { entityType: string }) {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editField, setEditField] = useState<CustomField | null>(null);
  const [form, setForm] = useState<FieldForm>(emptyForm);

  const { data: fields, isLoading } = useQuery<CustomField[]>({
    queryKey: [`/api/admin/custom-fields?entityType=${entityType}`],
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await apiRequest("POST", "/api/admin/custom-fields", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/custom-fields"] });
      setCreateOpen(false);
      setForm(emptyForm);
      toast({ title: "Custom field created" });
    },
    onError: () => {
      toast({ title: "Failed to create field", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      await apiRequest("PUT", `/api/admin/custom-fields/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/custom-fields"] });
      setEditField(null);
      setForm(emptyForm);
      toast({ title: "Custom field updated" });
    },
    onError: () => {
      toast({ title: "Failed to update field", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/custom-fields/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/custom-fields"] });
      toast({ title: "Custom field deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete field", variant: "destructive" });
    },
  });

  const handleLabelChange = (label: string, autoKey: boolean) => {
    setForm((prev) => ({
      ...prev,
      label,
      fieldKey: autoKey ? toSnakeCase(label) : prev.fieldKey,
    }));
  };

  const buildPayload = () => {
    const payload: Record<string, unknown> = {
      entityType,
      fieldKey: form.fieldKey,
      label: form.label,
      fieldType: form.fieldType,
      isRequired: form.isRequired,
      sortOrder: form.sortOrder,
    };
    if (form.fieldType === "select" && form.options.trim()) {
      payload.options = form.options.split(",").map((o) => o.trim()).filter(Boolean);
    } else {
      payload.options = null;
    }
    return payload;
  };

  const handleCreate = () => {
    createMutation.mutate(buildPayload());
  };

  const handleUpdate = () => {
    if (!editField) return;
    updateMutation.mutate({ id: editField.id, data: buildPayload() });
  };

  const openEdit = (field: CustomField) => {
    setEditField(field);
    const opts = Array.isArray(field.options) ? (field.options as string[]).join(", ") : "";
    setForm({
      label: field.label,
      fieldKey: field.fieldKey,
      fieldType: field.fieldType,
      isRequired: field.isRequired,
      sortOrder: field.sortOrder,
      options: opts,
    });
  };

  const sorted = fields ? [...fields].sort((a, b) => a.sortOrder - b.sortOrder) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {sorted.length} field{sorted.length !== 1 ? "s" : ""} defined
        </p>
        <Button onClick={() => { setForm(emptyForm); setCreateOpen(true); }} data-testid={`button-add-${entityType}-field`}>
          <Plus className="w-4 h-4 mr-2" /> Add Field
        </Button>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">Loading fields...</div>
      )}

      {!isLoading && sorted.length === 0 && (
        <div className="text-center py-12" data-testid={`text-empty-${entityType}`}>
          <SlidersHorizontal className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No custom fields defined for {entityType}s yet.</p>
        </div>
      )}

      <div className="space-y-3">
        {sorted.map((field) => (
          <Card key={field.id} data-testid={`card-field-${field.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium" data-testid={`text-field-label-${field.id}`}>{field.label}</span>
                    <Badge variant="secondary" data-testid={`badge-field-type-${field.id}`}>
                      {fieldTypeLabels[field.fieldType] || field.fieldType}
                    </Badge>
                    {field.isRequired && (
                      <Badge variant="default" data-testid={`badge-required-${field.id}`}>Required</Badge>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground" data-testid={`text-field-key-${field.id}`}>
                    Key: {field.fieldKey}
                  </span>
                  <span className="text-xs text-muted-foreground" data-testid={`text-field-sort-${field.id}`}>
                    Sort order: {field.sortOrder}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => openEdit(field)}
                    data-testid={`button-edit-field-${field.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`button-delete-field-${field.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Custom Field</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{field.label}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteMutation.mutate(field.id)}
                          data-testid="button-confirm-delete"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
          </DialogHeader>
          <FieldFormFields
            form={form}
            onLabelChange={(v) => handleLabelChange(v, true)}
            onChange={setForm}
            testPrefix="create"
          />
          <Button
            className="w-full"
            onClick={handleCreate}
            disabled={createMutation.isPending || !form.label.trim() || !form.fieldKey.trim()}
            data-testid="button-submit-field"
          >
            {createMutation.isPending ? "Creating..." : "Create Field"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editField} onOpenChange={(open) => { if (!open) { setEditField(null); setForm(emptyForm); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Custom Field</DialogTitle>
          </DialogHeader>
          <FieldFormFields
            form={form}
            onLabelChange={(v) => handleLabelChange(v, false)}
            onChange={setForm}
            testPrefix="edit"
          />
          <Button
            className="w-full"
            onClick={handleUpdate}
            disabled={updateMutation.isPending || !form.label.trim() || !form.fieldKey.trim()}
            data-testid="button-update-field"
          >
            {updateMutation.isPending ? "Updating..." : "Update Field"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldFormFields({
  form,
  onLabelChange,
  onChange,
  testPrefix,
}: {
  form: FieldForm;
  onLabelChange: (label: string) => void;
  onChange: (form: FieldForm) => void;
  testPrefix: string;
}) {
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <label className="text-sm font-medium">Label</label>
        <Input
          placeholder="e.g. Floor Number"
          value={form.label}
          onChange={(e) => onLabelChange(e.target.value)}
          data-testid={`input-${testPrefix}-label`}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Field Key</label>
        <Input
          placeholder="e.g. floor_number"
          value={form.fieldKey}
          onChange={(e) => onChange({ ...form, fieldKey: e.target.value })}
          data-testid={`input-${testPrefix}-field-key`}
        />
        <p className="text-xs text-muted-foreground">Auto-generated from label. You can customize it.</p>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Field Type</label>
        <Select value={form.fieldType} onValueChange={(v) => onChange({ ...form, fieldType: v })}>
          <SelectTrigger data-testid={`select-${testPrefix}-field-type`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Text</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
            <SelectItem value="select">Select</SelectItem>
            <SelectItem value="date">Date</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {form.fieldType === "select" && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Options (comma-separated)</label>
          <Input
            placeholder="e.g. Option A, Option B, Option C"
            value={form.options}
            onChange={(e) => onChange({ ...form, options: e.target.value })}
            data-testid={`input-${testPrefix}-options`}
          />
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-medium">Required</label>
        <Switch
          checked={form.isRequired}
          onCheckedChange={(v) => onChange({ ...form, isRequired: v })}
          data-testid={`switch-${testPrefix}-required`}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Sort Order</label>
        <Input
          type="number"
          placeholder="0"
          value={form.sortOrder}
          onChange={(e) => onChange({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
          data-testid={`input-${testPrefix}-sort-order`}
        />
      </div>
    </div>
  );
}

export default function AdminCustomFields() {
  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Custom Fields</h1>
        <p className="text-muted-foreground mt-2">Define custom data fields for your apartments and reservations.</p>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground" data-testid="text-info">
            Custom fields allow you to add extra data to your apartments or reservations.
            Define fields here and they will be available when creating or editing those entities.
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue="apartment" data-testid="tabs-entity-type">
        <TabsList data-testid="tabs-list">
          <TabsTrigger value="apartment" data-testid="tab-apartment">Apartment Fields</TabsTrigger>
          <TabsTrigger value="reservation" data-testid="tab-reservation">Reservation Fields</TabsTrigger>
        </TabsList>
        <TabsContent value="apartment">
          <FieldList entityType="apartment" />
        </TabsContent>
        <TabsContent value="reservation">
          <FieldList entityType="reservation" />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
